import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  increment,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { Conversation, Message, ConversationType } from "@visitproof/types";

const conversationsRef = collection(db, "conversations");

const MessagingService = {
  /**
   * Get or create a conversation with a parent or supervisor.
   */
  async getOrCreateConversation(params: {
    accountId: string;
    type: ConversationType;
    /** Parent or supervisor record ID */
    recordId: string;
    /** Their Firebase Auth user ID */
    userId?: string;
    /** Display name */
    name: string;
    currentUserId: string;
    caseId?: string;
  }): Promise<string> {
    const filterField = params.type === "parent" ? "parentId" : "supervisorId";

    // Check if conversation already exists
    const existingQuery = query(
      conversationsRef,
      where("accountId", "==", params.accountId),
      where(filterField, "==", params.recordId),
      where("status", "==", "active"),
    );

    const snapshot = await getDocs(existingQuery);

    if (!snapshot.empty) {
      const existing = snapshot.docs[0];
      const data = existing.data();
      if (!data.participantIds?.includes(params.currentUserId)) {
        await updateDoc(existing.ref, {
          participantIds: [...(data.participantIds || []), params.currentUserId],
        });
      }
      return existing.id;
    }

    // Create new conversation
    const participantIds = [params.currentUserId];
    if (params.userId && !participantIds.includes(params.userId)) {
      participantIds.push(params.userId);
    }

    const conversationData: Record<string, any> = {
      accountId: params.accountId,
      participantIds,
      type: params.type,
      participantName: params.name,
      parentName: params.name, // backwards compat
      parentUserId: params.userId || null,
      caseId: params.caseId || null,
      status: "active",
      lastMessage: null,
      lastMessageAt: null,
      lastMessageBy: null,
      unreadCount: {},
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    if (params.type === "parent") {
      conversationData.parentId = params.recordId;
    } else {
      conversationData.supervisorId = params.recordId;
    }

    const newConversation = await addDoc(conversationsRef, conversationData);
    return newConversation.id;
  },

  /**
   * Subscribe to conversations for an account (admin view).
   */
  subscribeToConversations(
    accountId: string,
    callback: (conversations: Conversation[]) => void,
    onError?: (error: Error) => void,
  ): () => void {
    const q = query(
      conversationsRef,
      where("accountId", "==", accountId),
      where("status", "==", "active"),
      orderBy("lastMessageAt", "desc"),
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const conversations = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as Conversation[];
        callback(conversations);
      },
      (error) => {
        console.error("Error subscribing to conversations:", error);
        onError?.(error);
      },
    );
  },

  /**
   * Subscribe to messages in a conversation.
   */
  subscribeToMessages(
    conversationId: string,
    callback: (messages: Message[]) => void,
    onError?: (error: Error) => void,
  ): () => void {
    const messagesRef = collection(db, "conversations", conversationId, "messages");
    const q = query(messagesRef, orderBy("createdAt", "asc"));

    return onSnapshot(
      q,
      (snapshot) => {
        const messages = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as Message[];
        callback(messages);
      },
      (error) => {
        console.error("Error subscribing to messages:", error);
        onError?.(error);
      },
    );
  },

  /**
   * Send a message in a conversation.
   */
  async sendMessage(params: {
    conversationId: string;
    senderId: string;
    senderName: string;
    senderRole: "admin" | "supervisor" | "parent";
    body: string;
    accountId: string;
    participantIds: string[];
  }): Promise<string> {
    const messagesRef = collection(db, "conversations", params.conversationId, "messages");

    const messageDoc = await addDoc(messagesRef, {
      conversationId: params.conversationId,
      senderId: params.senderId,
      senderName: params.senderName,
      senderRole: params.senderRole,
      body: params.body,
      type: "text",
      accountId: params.accountId,
      readBy: { [params.senderId]: true },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Update conversation with last message info
    const unreadUpdate: Record<string, any> = {};
    params.participantIds.forEach((pid) => {
      if (pid !== params.senderId) {
        unreadUpdate[`unreadCount.${pid}`] = increment(1);
      }
    });

    await updateDoc(doc(conversationsRef, params.conversationId), {
      lastMessage: params.body.length > 100 ? params.body.substring(0, 100) + "..." : params.body,
      lastMessageAt: serverTimestamp(),
      lastMessageBy: params.senderName,
      ...unreadUpdate,
      updatedAt: serverTimestamp(),
    });

    return messageDoc.id;
  },

  /**
   * Mark conversation as read for a user.
   */
  async markAsRead(conversationId: string, userId: string): Promise<void> {
    await updateDoc(doc(conversationsRef, conversationId), {
      [`unreadCount.${userId}`]: 0,
    });
  },
};

export default MessagingService;
